import type { GamificationEvent, GamificationLevel } from '../../../types/auth';

/**
 * CORE-946 — presentation model for the level-up celebration modal.
 *
 * The level a `level_up` event congratulates is carried by the event itself
 * (`referenceKey`, e.g. 'keeper'). The live gamification profile is a SEPARATE
 * source and can disagree with the event: a QA-injected event (CORE-900) never
 * moves the profile, and a real event can outrun the profile refresh. The modal
 * must therefore title, badge and colour the celebration from the EVENT, and only
 * borrow XP progress from the profile when the profile is already at that level.
 *
 * The bug (CORE-946): the modal preferred the live profile's level over the
 * event's `referenceKey`, so an event for «Хранитель» (keeper) rendered the
 * user's current «Искатель» (seeker).
 */

// Accent colour per stable level code (card border, name, progress). Colour is
// NOT applied to the medallion — the canonical asset carries its own palette.
export const LEVEL_STYLE: Record<string, { color: string }> = {
  seeker: { color: '#3E8E6B' },
  keeper: { color: '#2E7D52' },
  master: { color: '#9B5DE5' },
  grand_master: { color: '#E85D04' },
};

export const LEVEL_NAME_FALLBACK: Record<string, string> = {
  seeker: 'Искатель', keeper: 'Хранитель', master: 'Проводник света', grand_master: 'Магистр света',
};

export interface LevelUpPresentation {
  /** Stable level code being celebrated — always from the event. */
  code: string;
  /** Localized level name for the code. */
  levelName: string;
  /** Accent colour for the code. */
  color: string;
  /**
   * Profile level to draw the XP progress bar from, or `null` when the fetched
   * profile is at a different level than the event celebrates (then no bar is
   * shown for that other level).
   */
  progressLevel: GamificationLevel | null;
}

export function resolveLevelUpPresentation(
  event: GamificationEvent,
  profileLevel: GamificationLevel | null,
): LevelUpPresentation {
  const code = event.referenceKey ?? profileLevel?.level ?? 'seeker';
  const style = LEVEL_STYLE[code] ?? LEVEL_STYLE.seeker;
  const levelName = LEVEL_NAME_FALLBACK[code] ?? event.title ?? profileLevel?.name ?? '';
  const progressLevel = profileLevel && profileLevel.level === code ? profileLevel : null;
  return { code, levelName, color: style.color, progressLevel };
}
